//Presets
import router from "./router";
import App from "./App.vue";
import i18n from "./i18n";
import intersect from "./directives/v-intersect";
import Lara from "@primeuix/themes/lara";
import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
// Styles
import "./style.css";
import "primeicons/primeicons.css";
//Primevue
import ToastService from "primevue/toastservice";
import DialogService from "primevue/dialogservice";
import ConfirmationService from "primevue/confirmationservice";
import BadgeDirective from "primevue/badgedirective";
import PrimeVue, { defaultOptions } from "primevue/config";

import Ripple from "primevue/ripple";
import Tooltip from "primevue/tooltip";
import AnimateOnScroll from "primevue/animateonscroll";
//Pinia
import { createPinia } from "pinia";
import { definePreset } from "@primeuix/themes";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { queryClient } from "./queryClient";

const theme = definePreset(Lara, {
  semantic: {
    formField: {
      paddingX: "0.75rem",
      paddingY: "0.5rem",
    },
  },
});
const app = createApp(App);
const pinia = createPinia();
const { t } = i18n.global;

Sentry.init({
  app,
  dsn: "https://9a664e5aaead2b96ebf450c25d5116f5@o378605.ingest.us.sentry.io/4511862943645696",
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
    // userInfo: false,
    // httpBodies: [],
  },
});

// Suppress known PrimeVue 4.3.x warning about ChevronDownIcon label prop/computed conflict
app.config.warnHandler = (msg, instance, trace) => {
  // Suppress the specific warning about computed property "label" already defined in Props
  if (msg.includes('Computed property "label" is already defined in Props')) {
    return;
  }
  // Log other warnings normally
  console.warn(msg, trace);
};

const primeVueLocalization = {
  startsWith: t("Starts with"),
  contains: t("Contains"),
  notContains: t("Not contains"),
  endsWith: t("Ends with"),
  equals: t("Equals"),
  notEquals: t("Not equals"),
  noFilter: t("No Filter"),
  lt: t("Less than"),
  lte: t("Less than or equal to"),
  gt: t("Greater than"),
  gte: t("Greater than or equal to"),
  dateIs: t("Date is"),
  dateIsNot: t("Date is not"),
  dateBefore: t("Date is before"),
  dateAfter: t("Date is after"),
  clear: t("Clear"),
  apply: t("Apply"),
  matchAll: t("Match All"),
  matchAny: t("Match Any"),
  dayNamesMin: [t("Su"), t("Mo"), t("Tu"), t("We"), t("Th"), t("Fr"), t("Sa")],
  monthNames: [t("January"), t("February"), t("March"), t("April"), t("May"), t("June"), t("July"), t("August"), t("September"), t("October"), t("November"), t("December")],
  emptyMessage: t("no-available-options"),
};

app.use(router);
app.use(ToastService);
app.use(DialogService);
app.use(PrimeVue, {
  ripple: true,
  locale: {
    ...defaultOptions.locale, // use default values for everything else
    firstDayOfWeek: 1, // change to Monday
    ...primeVueLocalization,
  },
  theme: {
    preset: theme,
    options: {
      darkModeSelector: ".dark",
      cssLayer: {
        name: "primevue",
        order: "theme, base, primevue",
      },
    },
  },
});
app.use(i18n);
app.use(pinia);
app.use(ConfirmationService);
app.use(VueQueryPlugin, {
  queryClient,
  enableDevtoolsV6Plugin: true,
});
app.directive("ripple", Ripple);
app.directive("tooltip", Tooltip);
app.directive("badge", BadgeDirective);
app.directive("intersect", intersect);
app.directive("animateOnScroll", AnimateOnScroll);

// Normalize decimal separator in InputNumber fields to always match the app locale.
// "." and "," both insert the correct separator (e.g. "," in es/ca/it, "." in en).
document.addEventListener(
  "keydown",
  (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement;
    if (!target?.classList.contains("p-inputnumber-input")) return;
    if (event.code !== "NumpadDecimal" && event.key !== "." && event.key !== ",") return;

    const separator = new Intl.NumberFormat(String(i18n.global.locale)).formatToParts(1.1).find((p) => p.type === "decimal")?.value ?? ".";

    if (event.key === separator) return;

    event.preventDefault();
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;
    target.value = target.value.slice(0, start) + separator + target.value.slice(end);
    target.setSelectionRange(start + 1, start + 1);
    target.dispatchEvent(new Event("input", { bubbles: true }));
  },
  true,
);

app.mount("#app");
